import {
  GetFragmentDto,
  ListEditsVideoEditorEditsQueryResponse,
} from '@app/modules/fragments/fragment.types';
import { QueriesView } from '@mi-front/ui';
import { Box, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';

import { SocialExportUploadPageContent } from './SocialExportUploadPageContent';
import { SocialExportUploadPageSkeleton } from './SocialExportUploadPageSkeleton';
import {
  type UseSocialExportUploadPageResult,
  useSocialExportUploadPage,
} from './useSocialExportUploadPage';

/** Wires async queries to the social export upload UI for a fragment and platform. */
export function SocialExportUploadPage() {
  const uploadPage: UseSocialExportUploadPageResult = useSocialExportUploadPage();

  if (!uploadPage.areConnectionsResolved) {
    return <SocialExportUploadPageSkeleton />;
  }

  if (uploadPage.connections.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="text.secondary">
          <FormattedMessage
            id="socialExport.connections.noneAssignedForStation"
            defaultMessage="No hay cuentas conectadas asignadas a esta emisora para esta plataforma."
          />
        </Typography>
      </Box>
    );
  }

  if (!uploadPage.selectedConnectionId) {
    return <SocialExportUploadPageSkeleton />;
  }

  // settingsQuery is intentionally omitted: changing the selected connection refetches
  // settings, and gating the whole page on it would remount into the skeleton.
  return (
    <QueriesView<[GetFragmentDto, string, ListEditsVideoEditorEditsQueryResponse]>
      loader={<SocialExportUploadPageSkeleton />}
      queries={[uploadPage.fragmentQuery, uploadPage.mediaUrlQuery, uploadPage.editsListQuery]}
    >
      {([fragment, mediaUrl]) => (
        <SocialExportUploadPageContent
          fragment={fragment}
          fragmentMediaUrl={mediaUrl}
          settingsQuery={uploadPage.settingsQuery}
          platform={uploadPage.platform}
          selectedSource={uploadPage.selectedSource}
          onSelectedSourceChange={uploadPage.setSelectedSource}
          edits={uploadPage.edits}
          isFragmentSourceAllowed={uploadPage.isFragmentSourceAllowed}
          hasAvailableSources={uploadPage.hasAvailableSources}
          showMediaSelector={uploadPage.showMediaSelector}
          hasSelectedRenderedEdit={uploadPage.hasSelectedRenderedEdit}
          renderedVideoUrl={uploadPage.renderedVideoUrl}
          PublishForm={uploadPage.PublishForm}
          onUpload={(data) => uploadPage.handleUpload(fragment, data)}
          connections={uploadPage.connections}
          selectedConnectionId={uploadPage.selectedConnectionId}
          onSelectedConnectionChange={uploadPage.setSelectedConnectionId}
        />
      )}
    </QueriesView>
  );
}
